import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Fade,
  Grid,
  Grow,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  createTheme,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  Article as ArticleIcon,
  Facebook,
  WhatsApp,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import logo1 from "../../assets/images/logo1.png";
import defaultAvatar from "../../assets/images/default.png";
import baseTheme from "../../theme/theme";

const ModernTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 18,
    backgroundColor: alpha(theme.palette.common.white, 0.9),
    boxShadow: "0 12px 35px rgba(15,23,42,0.08)",
    transition: "all 0.25s ease",
    "& fieldset": {
      borderColor: alpha(theme.palette.primary.main, 0.2),
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused": {
      boxShadow: `0 15px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
    "&.Mui-focused fieldset": {
      borderWidth: 1.5,
      borderColor: theme.palette.primary.main,
    },
  },
}));

const Login = () => {
  /* —————————————————— estado local —————————————————— */
  const [loading, setLoading] = useState(false);
  const [dataNutriologoSubdominio, setDataNutriologoSubdominio] =
    useState(null);
  const [errorSubdominio, setErrorSubdominio] = useState(null);

  const [telefono, setTelefono] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  /* —————————————————— hooks / context —————————————————— */
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  /* —————————————————— obtener info del nutriólogo según subdominio —————————————————— */
  const fetchDataNutriologo = async () => {
    try {
      setLoading(true);
      const hostname = window.location.hostname;
      const subdominio = hostname.split(".")[0];
      if (!subdominio) throw new Error("No se pudo determinar el subdominio.");

      const response = await fetch(
        `/api/nutriologo/subdomain/info?subdominio=${subdominio}`
      );
      if (!response.ok)
        throw new Error("Error al obtener los datos del backend");
      const result = await response.json();
      setDataNutriologoSubdominio(result);
    } catch (err) {
      setErrorSubdominio(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataNutriologo();
  }, []);

  /* —————————————————— tema dinámico (usa color3) —————————————————— */
  const nutriologoInfo = dataNutriologoSubdominio?.nutriologoInfo || {};
  const brandPrimary = nutriologoInfo.color1 || "rgb(142, 45, 226)";
  const brandSecondary = nutriologoInfo.color2 || "rgb(74, 0, 224)";
  const accentColor = nutriologoInfo.color3 || "#4f74fe";
  const primaryGradient = `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})`;
  const glassOverlay = alpha("#ffffff", 0.12);

  const customTheme = useMemo(() => {
    return createTheme({
      ...baseTheme,
      palette: {
        ...baseTheme.palette,
        primary: { ...baseTheme.palette.primary, main: accentColor },
      },
    });
  }, [accentColor]);

  /* —————————————————— avatar dinámico —————————————————— */
  const avatarSrc = useMemo(() => {
    const img = nutriologoInfo?.usuario?.imagen;
    if (!img || img === "" || img === "default" || img === "default.png") {
      return defaultAvatar;
    }
    return img;
  }, [nutriologoInfo]);

  const displayName = nutriologoInfo?.nombreAMostrar?.trim()
    ? nutriologoInfo.nombreAMostrar
    : `${nutriologoInfo?.usuario?.nombre || ""} ${
        nutriologoInfo?.usuario?.apellidos || ""
      }`.trim() || "tu especialista";
  const occupationLabel = (
    nutriologoInfo?.giro || "Asesor nutricional"
  ).toUpperCase();
  const ownerLabel = nutriologoInfo?.giro || "especialista";
  const highlightedSignature = displayName;
  const heroHighlights = [
    nutriologoInfo?.especialidad && {
      label: "Especialidad",
      value: nutriologoInfo.especialidad,
    },
    nutriologoInfo?.modalidadConsulta && {
      label: "Modalidad",
      value: nutriologoInfo.modalidadConsulta,
    },
    typeof nutriologoInfo?.aniosExperiencia === "number" && {
      label: "Experiencia",
      value: `${nutriologoInfo.aniosExperiencia} años en consulta`,
    },
  ].filter(Boolean);
  const supportBullets = [
    "Seguimiento puntual",
    "Mensajes directos",
    "Historial centralizado",
  ];
  const loginHighlights = [
    {
      title: "Acceso seguro",
      detail: `Solo tu ${ownerLabel} verá tus datos`,
    },
    {
      title: "Nuevo: Recomendaciones",
      detail: "Encuentra las indicaciones compartidas durante tu plan.",
    },
  ];
  const publicProfileUrl = nutriologoInfo?.subdomain
    ? `https://${nutriologoInfo.subdomain}.nutrilander.com/perfil`
    : "https://nutrilander.com";

  const openLink = (url, options = { newTab: true }) => {
    if (!url) return;
    if (options.newTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  };

  /* —————————————————— submit —————————————————— */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // obtenemos el _id del nutriólogo para enviarlo como idNutriologo
    const idNutriologo = dataNutriologoSubdominio?.nutriologoInfo?._id;
    if (!idNutriologo) {
      setError("No se encontró el id del nutriólogo.");
      setSnackbarOpen(true);
      return;
    }

    const result = await login(telefono, password, idNutriologo);
    if (result && !result.success) {
      setError(result.message || "Error desconocido");
      setSnackbarOpen(true);
    }
  };

  /* —————————————————— colores por defecto —————————————————— */
  return (
    <ThemeProvider theme={customTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          background: `radial-gradient(circle at 20% 20%, ${alpha(
            brandPrimary,
            0.18
          )}, ${alpha(brandSecondary, 0.05)} 45%, #f8fafc)`,
          py: { xs: 4, sm: 6 },
          px: { xs: 2, sm: 3, md: 5 },
        }}
      >
        <Grid
          container
          spacing={{ xs: 4, md: 5 }}
          flexDirection={{ xs: "column", md: "row" }}
          alignItems="stretch"
        >
          <Grid item xs={12} md={6}>
            <Grow in timeout={900}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: { xs: 4, md: 5 },
                  overflow: "hidden",
                  background: primaryGradient,
                  color: "#fff",
                  height: "100%",
                  minHeight: { xs: 520, md: "100%" },
                  p: { xs: 4, sm: 5 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: `0 40px 90px ${alpha(brandPrimary, 0.35)}`,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: `radial-gradient(circle at 20% 25%, ${alpha(
                      "#ffffff",
                      0.25
                    )}, transparent 50%)`,
                  }}
                />

                <Stack spacing={3} sx={{ position: "relative" }}>
                  <Stack direction="row" spacing={2.5} alignItems="center">
                    <Avatar
                      src={avatarSrc}
                      alt="Nutriólogo"
                      sx={{
                        width: 96,
                        height: 96,
                        border: `3px solid ${glassOverlay}`,
                        boxShadow: `0 15px 35px ${alpha("#000", 0.35)}`,
                      }}
                    />
                    <Box>
                      <Typography variant="overline" sx={{ letterSpacing: 3 }}>
                        {occupationLabel}
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight={800}
                        lineHeight={1.1}
                      >
                        {displayName}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {nutriologoInfo?.textoBienvenidaAPacientes ||
                          "Ingreso al panel de seguimiento para continuar con tus indicaciones personalizadas."}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box>
                    <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                      {nutriologoInfo?.textoSegundoBienvenidaAPacientes ||
                        "Este portal reúne tu información y te mantiene conectado con tu especialista."}
                    </Typography>
                    {heroHighlights.length ? (
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                          },
                          gap: 2,
                          mt: 3,
                        }}
                      >
                        {heroHighlights.map((item) => (
                          <Paper
                            key={item.label}
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 3,
                              backgroundColor: alpha("#000", 0.18),
                              backdropFilter: "blur(16px)",
                              color: "inherit",
                            }}
                          >
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              {item.label}
                            </Typography>
                            <Typography variant="h6" fontWeight={700}>
                              {item.value}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          mt: 3,
                          p: 3,
                          borderRadius: 3,
                          backgroundColor: alpha("#000", 0.2),
                          backdropFilter: "blur(16px)",
                          color: "inherit",
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                          Tu especialista comparte la información clave durante
                          el registro.
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          mt={2}
                        >
                          {supportBullets.map((label) => (
                            <Chip
                              key={label}
                              label={label}
                              variant="outlined"
                              sx={{
                                borderColor: alpha("#fff", 0.5),
                                color: "inherit",
                                borderRadius: 999,
                              }}
                            />
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Box>
                </Stack>

                <Stack spacing={2.5} sx={{ position: "relative" }}>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    {nutriologoInfo?.telefonoAMostrar && (
                      <Tooltip title="Escríbenos por WhatsApp">
                        <IconButton
                          size="large"
                          sx={{
                            backgroundColor: alpha("#fff", 0.2),
                            color: "#fff",
                            borderRadius: 2,
                            border: `1px solid ${glassOverlay}`,
                            "&:hover": { backgroundColor: alpha("#fff", 0.32) },
                          }}
                          onClick={() =>
                            openLink(
                              `https://wa.me/52${nutriologoInfo.telefonoAMostrar}`
                            )
                          }
                        >
                          <WhatsApp />
                        </IconButton>
                      </Tooltip>
                    )}
                    {nutriologoInfo?.urlFacebook && (
                      <Tooltip title="Visítanos en Facebook">
                        <IconButton
                          size="large"
                          sx={{
                            backgroundColor: alpha("#fff", 0.2),
                            color: "#fff",
                            borderRadius: 2,
                            border: `1px solid ${glassOverlay}`,
                            "&:hover": { backgroundColor: alpha("#fff", 0.32) },
                          }}
                          onClick={() =>
                            openLink(
                              nutriologoInfo.urlFacebook.startsWith("http")
                                ? nutriologoInfo.urlFacebook
                                : `https://${nutriologoInfo.urlFacebook}`
                            )
                          }
                        >
                          <Facebook />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Button
                      variant="contained"
                      color="inherit"
                      startIcon={<ArticleIcon />}
                      onClick={() =>
                        openLink(publicProfileUrl, { newTab: false })
                      }
                      sx={{
                        color: brandPrimary,
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: 2,
                      }}
                    >
                      Ir al perfil público
                    </Button>
                  </Stack>

                  {(nutriologoInfo?.subdomain ||
                    nutriologoInfo?.telefonoAMostrar ||
                    nutriologoInfo?.direccionAMostrar) && (
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {nutriologoInfo?.subdomain && (
                        <>
                          {nutriologoInfo.subdomain}.nutrilander.com
                          <br />
                        </>
                      )}
                      {nutriologoInfo?.telefonoAMostrar && (
                        <>
                          Teléfono: {nutriologoInfo.telefonoAMostrar}
                          <br />
                        </>
                      )}
                      {nutriologoInfo?.direccionAMostrar && (
                        <>Dirección: {nutriologoInfo.direccionAMostrar}</>
                      )}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Grow>
          </Grid>

          <Grid item xs={12} md={6}>
            <Fade in timeout={900}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 3, sm: 4 },
                  borderRadius: { xs: 4, md: 5 },
                  boxShadow: "0 25px 80px rgba(15,23,42,0.12)",
                  border: `1px solid ${alpha(brandPrimary, 0.12)}`,
                  backgroundColor: "#fff",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Stack spacing={3} sx={{ position: "relative", zIndex: 1 }}>
                  <Stack spacing={1} alignItems="center">
                    <Box
                      component="img"
                      src={logo1}
                      alt="Nutriolab"
                      sx={{ height: 72 }}
                    />
                    <Chip
                      label="Portal de pacientes"
                      sx={{
                        backgroundColor: alpha(accentColor, 0.12),
                        color: accentColor,
                        letterSpacing: 1,
                        fontWeight: 600,
                      }}
                    />
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      textAlign="center"
                    >
                      Ingresa a tu panel
                    </Typography>
                    <Typography
                      variant="body1"
                      textAlign="center"
                      sx={{ color: "text.secondary" }}
                    >
                      Utiliza tu número celular y la contraseña compartida por{" "}
                      <Box
                        component="span"
                        sx={{ color: brandPrimary, fontWeight: 700 }}
                      >
                        {highlightedSignature}
                      </Box>
                      .
                    </Typography>
                  </Stack>

                  <Divider flexItem>
                    <Typography variant="caption" sx={{ letterSpacing: 2 }}>
                      Tu acceso
                    </Typography>
                  </Divider>

                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <ModernTextField
                      label="Número de teléfono"
                      fullWidth
                      required
                      margin="normal"
                      value={telefono}
                      onChange={(e) =>
                        setTelefono(
                          e.target.value.replace(/\D/g, "").slice(0, 10)
                        )
                      }
                      inputProps={{ maxLength: 10, inputMode: "numeric" }}
                    />
                    <ModernTextField
                      label="Contraseña"
                      fullWidth
                      required
                      margin="normal"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 2,
                        py: 1.4,
                        fontWeight: 700,
                        borderRadius: 2.5,
                      }}
                    >
                      Iniciar sesión
                    </Button>
                  </Box>

                  <Stack spacing={2}>
                    {loginHighlights.map((item) => (
                      <Paper
                        key={item.title}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          borderColor: alpha(brandPrimary, 0.15),
                          backgroundColor: alpha(brandPrimary, 0.02),
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {item.detail}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>

                  <Divider flexItem>
                    <Typography variant="caption" sx={{ letterSpacing: 2 }}>
                      ¿Aún no te registras?
                    </Typography>
                  </Divider>

                  <Button
                    component={RouterLink}
                    to="/registro"
                    variant="outlined"
                    fullWidth
                    sx={{
                      py: 1.2,
                      fontWeight: 700,
                      borderRadius: 2.5,
                      borderColor: accentColor,
                      color: accentColor,
                    }}
                  >
                    Registrarme como nuevo paciente
                  </Button>

                  <Stack spacing={1.5}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Si necesitas apoyo directo, usa los accesos rápidos:
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                      {nutriologoInfo?.telefonoAMostrar && (
                        <IconButton
                          sx={{
                            border: `1px solid ${alpha(brandPrimary, 0.2)}`,
                            color: "#25D366",
                            borderRadius: 2,
                          }}
                          onClick={() =>
                            openLink(
                              `https://wa.me/52${nutriologoInfo.telefonoAMostrar}`
                            )
                          }
                        >
                          <WhatsApp />
                        </IconButton>
                      )}
                      {nutriologoInfo?.urlFacebook && (
                        <IconButton
                          sx={{
                            border: `1px solid ${alpha(brandPrimary, 0.2)}`,
                            color: "#1877F2",
                            borderRadius: 2,
                          }}
                          onClick={() =>
                            openLink(
                              nutriologoInfo.urlFacebook.startsWith("http")
                                ? nutriologoInfo.urlFacebook
                                : `https://${nutriologoInfo.urlFacebook}`
                            )
                          }
                        >
                          <Facebook />
                        </IconButton>
                      )}
                      <Button
                        startIcon={<ArticleIcon />}
                        variant="text"
                        onClick={() =>
                          openLink(publicProfileUrl, { newTab: false })
                        }
                        sx={{ fontWeight: 700, textTransform: "none" }}
                      >
                        Ir al perfil público
                      </Button>
                    </Stack>
                    {(nutriologoInfo?.telefonoAMostrar ||
                      nutriologoInfo?.direccionAMostrar) && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {nutriologoInfo?.telefonoAMostrar && (
                          <>Teléfono: {nutriologoInfo.telefonoAMostrar} · </>
                        )}
                        {nutriologoInfo?.direccionAMostrar &&
                          `Dirección: ${nutriologoInfo.direccionAMostrar}`}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </Fade>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity="error">
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default Login;
